import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';

export type EvalPhase = 'idle' | 'running' | 'errored' | 'judging' | 'complete';

interface EvalRunState {
  phase: EvalPhase;
  comparisonId: string | null;
  candidateBranch: string;
  baselineBranch: string;
}

interface EvalRunActions {
  startRun: (candidateBranch: string, baselineBranch: string) => void;
  setPhase: (phase: EvalPhase) => void;
  setComparisonId: (id: string) => void;
}

const EvalRunContext = createContext<(EvalRunState & EvalRunActions) | null>(null);

export function EvalRunProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<EvalRunState>({
    phase: 'idle',
    comparisonId: null,
    candidateBranch: '',
    baselineBranch: '',
  });

  function startRun(candidateBranch: string, baselineBranch: string) {
    setState({ phase: 'running', comparisonId: null, candidateBranch, baselineBranch });
  }

  function setPhase(phase: EvalPhase) {
    setState((s) => ({ ...s, phase }));
  }

  function setComparisonId(id: string) {
    setState((s) => ({ ...s, comparisonId: id }));
  }

  return (
    <EvalRunContext.Provider value={{ ...state, startRun, setPhase, setComparisonId }}>
      {children}
    </EvalRunContext.Provider>
  );
}

export function useEvalRun() {
  const ctx = useContext(EvalRunContext);
  if (!ctx) throw new Error('useEvalRun must be used inside EvalRunProvider');
  return ctx;
}
